import { Stack, Typography, TextField, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useTBContext } from "../../Contexts/useTBStates";
import { StyledIconButton } from "../../../shared/Components/StyledIconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp';
import ArrowDownwardSharpIcon from '@mui/icons-material/ArrowDownwardSharp';


const stablefordPointsTemplate = [
    { scoreToPar: -3, points: 6, aceBonus: 0 },
    { scoreToPar: -2, points: 4, aceBonus: 0 },
    { scoreToPar: -1, points: 2 },
    { scoreToPar: 0, points: 1 },
    { scoreToPar: 1, points: 0 },
    { scoreToPar: 2, points: 0 }
]

const getCurrentStablefordPoints = (viewEvent: any, roundValue: number) => {
    const maybePoints = viewEvent?.rounds?.[roundValue - 1]?.holePoints;
    return Array.isArray(maybePoints) && maybePoints.length > 0
        ? maybePoints
        : stablefordPointsTemplate;
};

interface PointsTextFieldProps {
    factor: number;
    appTheme: any;
    value: any;
    type?: string;
    disabled?: boolean;
    onChange: (e: any) => void;
}

const PointsTextField = ({ factor, appTheme, ...rest }: PointsTextFieldProps) => (
    <TextField
        {...rest}
        size="small"
        sx={{
            width: 64 * (factor || 1),
            padding: 0,
            "& .MuiInputBase-input": {
                fontSize: 14 * (factor || 1),
                height: 24 * (factor || 1),
                padding: '4px 6px',
                textAlign: 'center',
            },
            '& .MuiOutlinedInput-root': { padding: 0 },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: appTheme.palette.primary.main,
            },
        }}
    />
);

const formatScoreToPar = (scoreToPar: number) => {
    if (scoreToPar === 0) return 'E';
    return scoreToPar > 0 ? `+${scoreToPar}` : String(scoreToPar);
};

export const StablefordPointsLayout = () => {

    const { viewEvent, roundValue, editMode, appTheme, factor, setViewEvent } = useTBContext();

    const [holePoints, setHolePoints] = useState(
        getCurrentStablefordPoints(viewEvent, roundValue));

    const maxScoreToPar = Math.max(...holePoints.map((item: any) => item.scoreToPar));
    const minScoreToPar = Math.min(...holePoints.map((item: any) => item.scoreToPar));
    const sortedHolePoints = [...holePoints].sort((a: any, b: any) => a.scoreToPar - b.scoreToPar);

    useEffect(() => {
        setHolePoints(getCurrentStablefordPoints(viewEvent, roundValue));
    }, [viewEvent, roundValue]);

    useEffect(() => {
        const savedHolePoints = viewEvent?.rounds?.[roundValue - 1]?.holePoints;
        if (!Array.isArray(savedHolePoints) || savedHolePoints.length === 0) {
            updateViewEvent([...stablefordPointsTemplate]);
        }
    }, [viewEvent, roundValue]);

    if (!viewEvent) return null;

    const updateViewEvent = (nextHolePoints: any[]) => {
        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        newViewEvent.rounds[roundValue - 1].holePoints = nextHolePoints;
        setViewEvent(JSON.parse(JSON.stringify(newViewEvent)));
    }

    const handlePointsChange = (scoreToPar: number, e: any) => {
        const newHolePoints = [...holePoints];
        const idx = newHolePoints.findIndex((hp: any) => Number(hp.scoreToPar) === Number(scoreToPar));
        if (idx < 0) return;
        newHolePoints[idx].points = Number(e.target.value);
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const handleAceBonusChange = (scoreToPar: number, e: any) => {
        const newHolePoints = [...holePoints];
        const idx = newHolePoints.findIndex((hp: any) => Number(hp.scoreToPar) === Number(scoreToPar));
        if (idx < 0) return;
        newHolePoints[idx].aceBonus = Number(e.target.value);
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const handleDeleteHole = (scoreToPar: number) => {
        const newHolePoints = holePoints.filter((hp: any) => Number(hp.scoreToPar) !== Number(scoreToPar));
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const handleAddBelow = (scoreToPar: number) => {
        const newHolePoints = [...holePoints];
        if (scoreToPar === maxScoreToPar) {
            newHolePoints.push({ scoreToPar: scoreToPar + 1, points: 0 });
        } else {
            newHolePoints.push({ scoreToPar: scoreToPar + 1, points: 0, aceBonus: 0 });
        }
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const handleAddAbove = (scoreToPar: number) => {
        const newHolePoints = [...holePoints, { scoreToPar: scoreToPar - 1, points: 0, aceBonus: 0 }];
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const cellSx = {
        fontSize: 13 * factor,
        fontWeight: 500,
        color: 'text.secondary',
        lineHeight: 1.3,
    };

    return (
        <Stack direction="column" spacing={1} sx={{ width: '100%', minWidth: 0, pt: 0.5 }}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                    width: '100%',
                    minWidth: 0,
                    pb: 0.5,
                    borderBottom: `1px dashed ${appTheme.palette.divider}`,
                }}
            >
                <Typography sx={{ ...cellSx, flex: 1.2, minWidth: 0 }}>To Par</Typography>
                <Typography sx={{ ...cellSx, flex: 1, minWidth: 0, textAlign: 'center' }}>Pts</Typography>
                <Typography sx={{ ...cellSx, flex: 1, minWidth: 0, textAlign: 'center' }}>Ace</Typography>
                {editMode && (
                    <Box sx={{ width: 72 * factor, flexShrink: 0 }} />
                )}
            </Stack>

            {sortedHolePoints.map((item: any) => {
                const isMin = Number(item.scoreToPar) === minScoreToPar;
                const isMax = Number(item.scoreToPar) === maxScoreToPar;

                return (
                    <Stack
                        key={item.scoreToPar}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: '100%', minWidth: 0 }}
                    >
                        <Typography
                            sx={{
                                flex: 1.2,
                                minWidth: 0,
                                fontSize: 14 * factor,
                                fontWeight: 600,
                                color: item.scoreToPar < 0 ? 'error.main' : 'text.primary',
                            }}
                        >
                            {formatScoreToPar(Number(item.scoreToPar))}
                        </Typography>

                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                            <PointsTextField
                                factor={factor}
                                appTheme={appTheme}
                                value={Number(item.points)}
                                type="number"
                                disabled={!editMode}
                                onChange={(e) => handlePointsChange(item.scoreToPar, e)}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                            {item.scoreToPar < -1 ? (
                                <PointsTextField
                                    factor={factor}
                                    appTheme={appTheme}
                                    value={Number(item.aceBonus)}
                                    type="number"
                                    disabled={!editMode}
                                    onChange={(e) => handleAceBonusChange(item.scoreToPar, e)}
                                />
                            ) : (
                                <Typography sx={{ fontSize: 13 * factor, color: 'text.disabled' }}>—</Typography>
                            )}
                        </Box>

                        {editMode && (
                            <Stack
                                direction="row"
                                spacing={0.25}
                                alignItems="center"
                                justifyContent="flex-end"
                                sx={{ width: 72 * factor, flexShrink: 0 }}
                            >
                                {isMin && (
                                    <StyledIconButton
                                        appTheme={appTheme}
                                        factor={factor}
                                        color="primary"
                                        width="max-content"
                                        height={28}
                                        icon={<><ArrowUpwardSharpIcon sx={{ fontSize: 14 }} /><AddIcon sx={{ fontSize: 14 }} /></>}
                                        label=""
                                        onClick={() => handleAddAbove(item.scoreToPar)}
                                    />
                                )}
                                {(isMin || isMax) && (
                                    <StyledIconButton
                                        appTheme={appTheme}
                                        factor={factor}
                                        color="error"
                                        width="max-content"
                                        height={28}
                                        icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                        label=""
                                        onClick={() => handleDeleteHole(item.scoreToPar)}
                                    />
                                )}
                                {isMax && (
                                    <StyledIconButton
                                        appTheme={appTheme}
                                        factor={factor}
                                        color="primary"
                                        width="max-content"
                                        height={28}
                                        icon={<><AddIcon sx={{ fontSize: 14 }} /><ArrowDownwardSharpIcon sx={{ fontSize: 14 }} /></>}
                                        label=""
                                        onClick={() => handleAddBelow(item.scoreToPar)}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                );
            })}
        </Stack>
    )
}
